import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaHome } from 'react-icons/fa';
import transactionService from '../services/transactionService';
import { formatCurrency } from '../utils/formatters';
import { useAppContext } from '../contexts/AppContext';
import { getCategoryTypeLabel, isExpenseType } from '../constants/categoryTypes';

const CategoryReport = () => {
  const { categories } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    months: 3 // Número de meses para comparação
  });

  // Definir datas padrão (últimos 3 meses)
  useEffect(() => {
    const today = new Date();
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Último dia do mês atual
    const startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1); // Primeiro dia de 3 meses atrás
    
    setFilters(prev => ({
      ...prev,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    }));
  }, []);

  // Função para calcular totais e subtotais
  const calculateTotals = (categories, months) => {
    const totals = {
      income: { name: 'Total Receitas', months: {} },
      expense: { name: 'Total Despesas', months: {} }
    };

    // Calcular subtotais por tipo de categoria
    const subtotalsByType = {};

    // Inicializar todos os subtotais primeiro
    categories.forEach(category => {
      const categoryType = category.type || 'OTHER';
      if (!subtotalsByType[categoryType]) {
        subtotalsByType[categoryType] = {
          name: `Subtotal ${getCategoryTypeLabel(categoryType)}`,
          months: months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {}),
          isSubtotal: true,
          type: categoryType
        };
      }
    });

    months.forEach(month => {
      totals.income.months[month.key] = 0;
      totals.expense.months[month.key] = 0;

      categories.forEach(category => {
        const value = category.months[month.key] || 0;
        const categoryType = category.type || 'OTHER';
        
        console.log(`🧮 Calculando ${category.name} (${categoryType}) - ${month.key}: ${value}`);
        
        // Somar no subtotal do tipo
        subtotalsByType[categoryType].months[month.key] += value;
        
        console.log(`📊 Subtotal ${categoryType} agora: ${subtotalsByType[categoryType].months[month.key]}`);

        if (value > 0) {
          totals.income.months[month.key] += value;
        } else if (value < 0) {
          totals.expense.months[month.key] += value;
        }
      });
    });

    // Log final dos subtotais
    Object.entries(subtotalsByType).forEach(([type, subtotal]) => {
      const totalByType = months.reduce((sum, month) => sum + (subtotal.months[month.key] || 0), 0);
      console.log(`🎯 Subtotal final ${type}: ${totalByType}`);
    });

    return { totals, subtotalsByType };
  };

  const processTransactionsByCategory = useCallback((transactions) => {
    console.log('🚀 Iniciando processamento de transações...');
    
    // Criar estrutura de meses para comparação
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const months = [];
    
    let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (currentDate <= endDate) {
      months.push({
        key: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
        label: currentDate.toLocaleDateString('pt-BR', { year: 'numeric', month: 'short' }),
        date: new Date(currentDate)
      });
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    console.log('📅 Meses do período:', months.map(m => m.label));

    // Agrupar transações por categoria
    const categoryData = {};
    
    // Inicializar categorias
    categories.forEach(category => {
      categoryData[category.id] = {
        id: category.id,
        name: category.name,
        code: category.id.toString().padStart(2, '0'),
        type: category.type, // Usar o tipo real da categoria
        categoryType: 'category',
        months: months.reduce((acc, month) => ({ ...acc, [month.key]: 0 }), {})
      };
    });

    console.log('🏷️ Categorias inicializadas:', Object.keys(categoryData).length);

    // Processar transações
    let processedCount = 0;
    transactions.forEach(transaction => {
      console.log('🔍 Processando transação:', {
        id: transaction.id,
        description: transaction.description?.substring(0, 30),
        amount: transaction.amount,
        nature: transaction.nature,
        categoryId: transaction.categoryId,
        category: transaction.category?.name,
        date: transaction.date
      });
      
      const transactionDate = new Date(transaction.date);
      const monthKey = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
      
      // Verificar se a transação tem categoria
      if (transaction.categoryId && categoryData[transaction.categoryId]) {
        // Corrigir: verificar se é INCOME (receita) ou EXPENSE (despesa)
        const amount = transaction.nature === 'EXPENSE' ? 
          -Math.abs(transaction.amount) : 
          Math.abs(transaction.amount);
        
        console.log(`✅ Adicionando ${amount} para categoria ${transaction.category?.name} no mês ${monthKey}`);
        categoryData[transaction.categoryId].months[monthKey] += amount;
        processedCount++;
      } else {
        console.log('❌ Transação ignorada:', {
          motivo: !transaction.categoryId ? 'Sem categoryId' : 'Categoria não encontrada',
          categoryId: transaction.categoryId,
          categoriaExiste: !!categoryData[transaction.categoryId]
        });
      }
    });

    console.log(`✅ Transações processadas: ${processedCount} de ${transactions.length}`);

    // Converter para array e ordenar
    const result = Object.values(categoryData)
      .filter(cat => {
        // Incluir apenas categorias que têm movimentação
        const hasMovement = Object.values(cat.months).some(value => value !== 0);
        if (hasMovement) {
          console.log(`💰 Categoria ${cat.name} tem movimentação:`, cat.months);
        }
        return hasMovement;
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    console.log('📋 Categorias com movimentação:', result.length);

    // Calcular totais e subtotais por tipo
    const { totals, subtotalsByType } = calculateTotals(result, months);
    
    console.log('🧮 Totais calculados:', totals);
    console.log('📊 Subtotais por tipo:', subtotalsByType);
    
    return { categories: result, totals, subtotalsByType, months };
  }, [filters, categories]);

  const loadReportData = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) return;
    
    try {
      setLoading(true);
      
      console.log('🔍 Carregando transações do período:', filters.startDate, 'até', filters.endDate);
      
      // Carregar todas as transações do período
      const transactions = await transactionService.search({
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      
      console.log('📊 Total de transações carregadas:', transactions.length);
      console.log('💾 Categorias disponíveis:', categories.length);
      
      // Processar dados por categoria e mês
      const processedData = processTransactionsByCategory(transactions);
      setReportData(processedData);
      
    } catch (error) {
      console.error('Erro ao carregar dados do relatório:', error);
      toast.error('Erro ao carregar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters.startDate, filters.endDate, categories, processTransactionsByCategory]);

  useEffect(() => {
    if (categories.length > 0) {
      loadReportData();
    }
  }, [loadReportData, categories]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    loadReportData();
  };

  const renderCategoriesByNature = (nature) => {
    if (!reportData.categories || !reportData.subtotalsByType) return null;

    const isIncome = nature === 'income';
    
    // Filtrar categorias usando lógica centralizada
    const filteredCategories = reportData.categories.filter(cat => {
      // Verificar se a categoria tem alguma movimentação
      const hasMovement = reportData.months.some(month => {
        const value = cat.months[month.key] || 0;
        return value !== 0;
      });
      
      if (!hasMovement) return false;
      
      // Usar função centralizada para determinar se é despesa ou receita
      return isExpenseType(cat.type) ? !isIncome : isIncome;
    });

    // Agrupar categorias por tipo
    const categoriesByType = {};
    filteredCategories.forEach(category => {
      const type = category.type || 'OTHER';
      if (!categoriesByType[type]) {
        categoriesByType[type] = [];
      }
      categoriesByType[type].push(category);
    });

    // Renderizar cada grupo de tipo
    const rows = [];
    Object.keys(categoriesByType).sort().forEach(type => {
      const typeCategories = categoriesByType[type];
      const subtotal = reportData.subtotalsByType[type];
      
      // Verificar se este tipo tem movimentação
      const hasMovementForNature = subtotal && reportData.months.some(month => {
        const value = subtotal.months[month.key] || 0;
        return value !== 0;
      });

      if (hasMovementForNature) {
        // 1. Categorias individuais do tipo
        typeCategories
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach(category => {
            rows.push(
              <tr key={category.id} className={isIncome ? "income-row" : "expense-row"}>
                <td>{category.code}</td>
                <td>{category.name}</td>
                {reportData.months.map(month => {
                  const value = category.months[month.key] || 0;
                  return (
                    <td key={month.key} className={`text-end ${value > 0 ? 'text-success' : 'text-danger'}`}>
                      {value !== 0 ? formatCurrency(value) : '-'}
                    </td>
                  );
                })}
              </tr>
            );
          });

        // 2. Subtotal do tipo
        rows.push(
          <tr key={`subtotal-${type}`} className="subtotal-row fw-bold" style={{ backgroundColor: isIncome ? '#e8f5e8' : '#f8e8e8' }}>
            <td></td>
            <td>{subtotal.name}</td>
            {reportData.months.map(month => {
              const value = subtotal.months[month.key] || 0;
              return (
                <td key={month.key} className={`text-end fw-bold ${value > 0 ? 'text-success' : 'text-danger'}`}>
                  {value !== 0 ? formatCurrency(value) : '-'}
                </td>
              );
            })}
          </tr>
        );

        // 3. Espaço entre tipos
        rows.push(
          <tr key={`space-${type}`}>
            <td colSpan={2 + reportData.months.length} style={{ height: '12px' }}></td>
          </tr>
        );
      }
    });

    return rows;
  };

  return (
    <Container>
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">Relatório por Categoria</h5>
          <div className="sheet-toolbar">
            <Button 
              as={Link}
              to="/reports"
              variant="outline-primary"
            >
              <FaHome className="me-1" /> Central de Relatórios
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          {/* Filtros */}
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Inicial</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Data Final</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button 
                variant="primary"
                onClick={handleApplyFilters}
                disabled={loading}
                className="mb-3"
              >
                {loading ? 'Carregando...' : 'Atualizar'}
              </Button>
            </Col>
          </Row>

          {/* Debug Info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mb-3 p-2 bg-light rounded">
              <small>
                <strong>Debug:</strong> {categories.length} categorias, {reportData.categories?.length || 0} com movimento
              </small>
            </div>
          )}

          {/* Tabela de Relatório */}
          {loading ? (
            <div className="text-center my-4">
              <span className="spinner-border text-primary" role="status"></span>
              <p className="mt-2">Gerando relatório...</p>
            </div>
          ) : reportData.months ? (
            <Table responsive hover className="excel-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>Cód.</th>
                  <th>Categoria</th>
                  {reportData.months.map(month => (
                    <th key={month.key} className="text-end">{month.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Receitas agrupadas por tipo */}
                {renderCategoriesByNature('income')}
                
                {/* Despesas agrupadas por tipo */}
                {renderCategoriesByNature('expense')}

                {/* Total Despesas */}
                <tr className="total-row fw-bold" style={{ backgroundColor: '#f8e8e8' }}>
                  <td></td>
                  <td>Total Despesas</td>
                  {reportData.months.map(month => (
                    <td key={month.key} className="text-end text-danger">
                      {formatCurrency(Math.abs(reportData.totals.expense.months[month.key] || 0))}
                    </td>
                  ))}
                </tr>
              </tbody>
            </Table>
          ) : (
            <div className="text-center my-4">
              <p>Selecione um período para gerar o relatório</p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default CategoryReport; 